/* eslint-disable no-console */

import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

import api, { api_without_cancellation } from "../../api/api";
import { getLoggedInFirmId } from "../../Utils/helper";
import { getOrganizationKey } from "../../Utils/chatSelection.mjs";

// Async Thunks
export const fetchFirms = createAsyncThunk("chat2/fetchFirms", async (_, { rejectWithValue }) => {
  try {
    const response = await api_without_cancellation.get("/api/chat/firms/", {});
    if (response.status !== 200) throw new Error("Failed to fetch firms");
    console.log(response.data);
    return await response.data;
  } catch (error) {
    return rejectWithValue(error.message);
  }
});

export const fetchUsers = createAsyncThunk(
  "chat2/fetchUsers",
  async ({ firmId, search = "" }, { rejectWithValue }) => {
    console.log(firmId);
    try {
      const params = new URLSearchParams({ limit: "30", search });
      const url = `/api/chat/firm/${firmId}/inbox/?${params.toString()}`;

      const response = await api_without_cancellation.get(url);
      const data = await response.data;
      const userData = [...(data.recent_threads || []), ...(data.users_without_threads || [])];
      console.log(userData);
      if (response.status !== 200) throw new Error("Failed to fetch users");
      return userData;
    } catch (error) {
      console.log(error);
      return rejectWithValue(error.message);
    }
  }
);

export const fetchCoPilotUsers = createAsyncThunk(
  "chat2/fetchCoPilotUsers",
  async ({ firmId, search = "" }, { rejectWithValue }) => {
    console.log(firmId);
    const loggedInFirmID = getLoggedInFirmId();
    try {
      const params = new URLSearchParams({ limit: "30", search, partner_copilot_firm_id: firmId });
      const url = `/api/chat/firm/${loggedInFirmID}/inbox/?${params.toString()}`;

      const response = await api_without_cancellation.get(url);
      const data = await response.data;
      const userData = [...(data.recent_threads || []), ...(data.users_without_threads || [])];
      console.log(userData);
      if (response.status !== 200) throw new Error("Failed to fetch users");
      return userData;
    } catch (error) {
      console.log(error);
      return rejectWithValue(error.message);
    }
  }
);

export const fetchProviderClient = createAsyncThunk(
  "chat2/fetchProviderClient",
  async ({ firmId, search = "" }, { rejectWithValue }) => {
    console.log(firmId);
    const loggedInFirmID = localStorage.getItem("firm_id");
    try {
      const params = new URLSearchParams({ limit: "30", search, partner_provider_id: firmId });
      const url = `/api/chat/firm/${loggedInFirmID}/inbox/?${params.toString()}`;

      const response = await api_without_cancellation.get(url);
      const data = await response.data;

      // Separate recent_threads and partner_cases
      const recentThreads = data.recent_threads || [];
      const partnerCases = data.partner_cases || [];

      // A case can have several provider treatments, so only match the individual
      // treatment assignment (case_provider_id), never the shared case_id.
      const existingCaseProviderIds = new Set(
        recentThreads
          .filter(
            (thread) => thread.case_provider_id !== undefined && thread.case_provider_id !== null
          )
          .map((thread) => String(thread.case_provider_id))
      );
      console.log(existingCaseProviderIds);

      // Filter out partner_cases that already have threads in recent_threads
      const filteredPartnerCases = partnerCases.filter(
        (partnerCase) => !existingCaseProviderIds.has(String(partnerCase.case_provider_id))
      );
      const partnerCaseMap = new Map(
        partnerCases.map((partnerCase) => [String(partnerCase.case_provider_id), partnerCase])
      );

      // Update each thread if a matching partnerCase exists
      const updatedThreads = recentThreads.map((thread) => {
        const match = partnerCaseMap.get(String(thread.case_provider_id));

        return {
          ...thread,
          ...(match && {
            case_id: match.case_id,
            case_display: match.case_display,
            case_number: match.case_number,
            client_name: match.client_name,
            client_avatar_url: match.client_avatar_url,
            case_type_name: match.case_type_name,
            case_type_icon: match.case_type_icon,
            incident_date: match.incident_date,
          }),
          has_thread: true,
          thread_id: thread.thread_id || thread.id,
          threadId: thread.threadId || thread.thread_id || thread.id,
        };
      });

      console.log(updatedThreads);

      // Combine the data: recent_threads first (with active chats), then new partner_cases
      const userData = [...updatedThreads, ...filteredPartnerCases];

      console.log({
        totalRecentThreads: recentThreads.length,
        totalPartnerCases: partnerCases.length,
        filteredPartnerCases: filteredPartnerCases.length,
        finalUserData: userData.length,
        skippedDuplicates: partnerCases.length - filteredPartnerCases.length,
      });

      if (response.status !== 200) throw new Error("Failed to fetch users");
      return userData;
    } catch (error) {
      console.log(error);
      return rejectWithValue(error.message);
    }
  }
);

export const createThread = createAsyncThunk(
  "chat2/createThread",
  async ({ threadType, memberIds, name }, { rejectWithValue }) => {
    try {
      console.log("Creating thread");
      const body = { thread_type: threadType, member_ids: memberIds };
      if (name) body.name = name;

      const response = await api.post("/api/chat/threads/", body);
      if (response.status !== 201) throw new Error("Failed to create thread");
      return await response.data;
    } catch (error) {
      console.log(error);
      return rejectWithValue(error.message);
    }
  }
);

export const createProviderThread = createAsyncThunk(
  "chat2/createProviderThread",
  async ({ caseProviderId, name }, { rejectWithValue }) => {
    try {
      console.log("Creating thread");
      const loggedInFirmID = localStorage.getItem("firm_id");

      const body = { case_provider_id: caseProviderId };
      if (name) body.name = name;

      const response = await api_without_cancellation.post(
        `/api/chat/firm/${loggedInFirmID}/inbox/`,
        body
      );
      console.log(response.status);
      if (response.status !== 200) throw new Error("Failed to create thread");
      return await response.data;
    } catch (error) {
      console.log(error);
      return rejectWithValue(error.message);
    }
  }
);

export const fetchMessages = createAsyncThunk(
  "chat2/fetchMessages",
  async ({ threadId, before, _limit = 50 }, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams({ limit: "250" });
      if (before) params.set("before", before);
      console.log(before);
      const url = `/api/chat/threads/${threadId}/messages/?${params.toString()}`;
      const response = await api_without_cancellation.get(url, {});
      if (!response.status === 200) throw new Error("Failed to fetch messages");
      const messages = await response.data;
      console.log(messages);
      return {
        threadId,
        messages: messages.results,
        before: messages.results[messages.results.length - 1]?.id || null,
        hasMore: messages.has_more,
      };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const markMessagesAsRead = createAsyncThunk(
  "chat2/markMessagesAsRead",
  async ({ threadId, messageIds }, { rejectWithValue }) => {
    try {
      const body = { message_ids: messageIds };
      const response = await api.post(`/api/chat/threads/${threadId}/mark-read/`, body);
      console.log("Mark as read response:", response.status);
      return { threadId, messageIds };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const initialState = {
  // Connection state
  wsConnected: false,
  connectionStatus: "disconnected", // 'connecting', 'connected', 'disconnected', 'error'

  // Data
  firms: [],
  selectedFirm: null,
  users: [],
  selectedUser: null,
  activeUsersRequestId: null,
  threads: {}, // threadId -> thread data
  messages: {}, // threadId -> { items: [], hasMore: boolean, loading: boolean, oldestMessageId: string }
  typingUsers: {}, // threadId -> { userId: timestamp }
  unreadCounts: {}, // threadId -> count
  latestMessages: {}, // threadId -> latest message content
  userOnlineStatus: {},

  // Notification counts
  notificationCounts: {
    global: {
      unread: 0,
    },
    firm: {},
    thread: {},
    lastUpdated: null,
  },

  // UI State
  loading: {
    firms: false,
    users: false,
    messages: false,
    creating: false,
  },
  error: {
    firms: null,
    users: null,
    messages: null,
    general: null,
  },

  // Search
  userSearch: "",

  // Current user
  currentUser: null,

  // Chat UI state
  isChatOpen: false,
  minimized: false,
};

const ChatReducer = createSlice({
  name: "chat2",
  initialState,
  reducers: {
    resetChatStateMountingChange: (state) => {
      // Reset everything except connection status
      state.selectedFirm = null;
      state.selectedUser = null;
      state.users = [];
      state.messages = {};
      state.unreadCounts = {};
      state.latestMessages = {};
      state.typingUsers = {};
      state.loading = {
        firms: false,
        users: false,
      };
      state.error = {
        firms: null,
        users: null,
      };
    },
    // Connection actions
    setConnectionStatus: (state, action) => {
      state.connectionStatus = action.payload;
      state.wsConnected = action.payload === "connected";
    },

    // Chat UI actions
    toggleChat: (state) => {
      state.isChatOpen = !state.isChatOpen;
    },

    openChat: (state) => {
      state.isChatOpen = true;
      state.minimized = false;
    },

    closeChat: (state) => {
      state.isChatOpen = false;
    },

    minimizeChat: (state) => {
      state.minimized = true;
    },

    maximizeChat: (state) => {
      state.minimized = false;
    },

    // Selection actions
    setSelectedFirm: (state, action) => {
      const isSameFirm =
        getOrganizationKey(state.selectedFirm) === getOrganizationKey(action.payload);
      state.selectedFirm = action.payload;
      // Only reset user state when switching to a different firm
      if (!isSameFirm) {
        state.selectedUser = null;
        state.users = [];
        state.userSearch = "";
        state.activeUsersRequestId = null;
        state.loading.users = false;
        state.error.users = null;
      }
    },

    setSelectedUser: (state, action) => {
      state.selectedUser = action.payload;
      // Initialize messages structure for this thread if it doesn't exist
      const threadId = action.payload?.threadId || action.payload?.id;
      if (threadId && !state.messages[threadId]) {
        state.messages[threadId] = {
          items: [],
          hasMore: true,
          loading: false,
          oldestMessageId: null,
        };
      }
    },

    setUserSearch: (state, action) => {
      state.userSearch = action.payload;
    },

    // Message actions
    addMessage: (state, action) => {
      const { threadId, message } = action.payload;
      console.log("Adding message to store:", message);

      if (!state.messages[threadId]) {
        state.messages[threadId] = {
          items: [],
          hasMore: true,
          loading: false,
          oldestMessageId: null,
        };
      }

      // Check if message already exists (prevent duplicates)
      const exists = state.messages[threadId].items.some(
        (m) =>
          (m.id && m.id === message.id) ||
          (m.client_message_id && m.client_message_id === message.client_message_id)
      );

      if (!exists) {
        // Ensure message has proper structure
        const processedMessage = {
          ...message,
          created_at: message.created_at || new Date().toISOString(),
          status: message.status || "delivered",
        };

        state.messages[threadId].items.push(processedMessage);

        // Sort by timestamp to maintain order
        state.messages[threadId].items.sort(
          (a, b) => new Date(a.created_at) - new Date(b.created_at)
        );
      }
    },

    updateMessage: (state, action) => {
      const { threadId, clientMessageId, serverMessage } = action.payload;
      console.log("Updating message:", { threadId, clientMessageId, serverMessage });

      if (state.messages[threadId]) {
        const messageIndex = state.messages[threadId].items.findIndex(
          (m) => m.client_message_id === clientMessageId
        );

        if (messageIndex !== -1) {
          // Update the message with server data
          state.messages[threadId].items[messageIndex] = {
            ...state.messages[threadId].items[messageIndex],
            ...serverMessage,
            // Use server timestamp if available
            created_at:
              serverMessage.created_at || state.messages[threadId].items[messageIndex].created_at,
            status: serverMessage.status || "sent",
          };

          console.log("Message updated:", state.messages[threadId].items[messageIndex]);
        }
      }
    },

    updateMessageStatus: (state, action) => {
      const { threadId, messageId, status } = action.payload;
      console.log("Updating message status:", { threadId, messageId, status });

      if (state.messages[threadId]) {
        const message = state.messages[threadId].items.find(
          (m) => m.id === messageId || m.client_message_id === messageId
        );

        if (message) {
          message.status = status;
          if (status === "read") {
            message.read = true;
          }
          console.log("Message status updated:", message);
        }
      }
    },

    updateMessageFromServer: (state, action) => {
      const { threadId, message } = action.payload;

      if (state.messages[threadId]) {
        const existingIndex = state.messages[threadId].items.findIndex(
          (m) => m.client_message_id === message.client_message_id
        );

        if (existingIndex !== -1) {
          // Update existing optimistic message
          state.messages[threadId].items[existingIndex] = {
            ...state.messages[threadId].items[existingIndex],
            ...message,
            status: "sent",
          };
        } else {
          // Add new message if not found
          state.messages[threadId].items.push({
            ...message,
            status: "delivered",
          });

          // Sort by timestamp
          state.messages[threadId].items.sort(
            (a, b) => new Date(a.created_at) - new Date(b.created_at)
          );
        }
      }
    },

    markMessageFailed: (state, action) => {
      const { threadId, clientMessageId } = action.payload;
      if (state.messages[threadId]) {
        const message = state.messages[threadId].items.find(
          (m) => m.client_message_id === clientMessageId
        );
        if (message) {
          message.status = "failed";
        }
      }
    },

    // Optimistic message addition
    addOptimisticMessage: (state, action) => {
      const { threadId, message } = action.payload;
      console.log("Adding optimistic message:", { threadId, message });

      if (!state.messages[threadId]) {
        state.messages[threadId] = {
          items: [],
          hasMore: false,
          loading: false,
          oldestMessageId: null,
        };
      }

      const optimisticMessage = {
        ...message,
        id: message.id || `temp_${message.client_message_id}`,
        status: "sending",
        // created_at: message.created_at || new Date().toISOString(),
      };

      state.messages[threadId].items.push(optimisticMessage);

      // Sort by timestamp
      // state.messages[threadId].items.sort(
      //   (a, b) => new Date(a.created_at) - new Date(b.created_at)
      // );
    },

    // Typing indicators
    setTypingUser: (state, action) => {
      const { threadId, userId, isTyping, userName } = action.payload;
      if (!state.typingUsers[threadId]) {
        state.typingUsers[threadId] = {};
      }

      if (isTyping) {
        // state.typingUsers[threadId][userId] = Date.now();
        state.typingUsers[threadId][userId] = {
          name: userName,
          timestamp: Date.now(),
        };
      } else {
        delete state.typingUsers[threadId][userId];
      }
    },

    // Clear old typing indicators (call this periodically)
    clearOldTypingIndicators: (state) => {
      const now = Date.now();
      const timeout = 5000; // 5 seconds

      Object.keys(state.typingUsers).forEach((threadId) => {
        Object.keys(state.typingUsers[threadId]).forEach((userId) => {
          if (now - state.typingUsers[threadId][userId] > timeout) {
            delete state.typingUsers[threadId][userId];
          }
        });
      });
    },

    // NEW: Online status management
    updateUserOnlineStatus: (state, action) => {
      const { userId, isOnline } = action.payload;

      console.log("Updating user online status:", { userId, isOnline });

      // 1. Find the user inside the array
      const foundUser = state.users.find(
        (user) => user?.other_participant_id === userId || user?.id === userId
      );

      // 2. Update user object directly (Immer allows mutation)
      if (foundUser) {
        foundUser.is_online = isOnline;
      }

      if (state.userOnlineStatus?.[userId]) {
        console.log(`User ${userId} status already exists, skipping update`);
        if (state.userOnlineStatus?.[userId]?.isOnline !== isOnline) {
          state.userOnlineStatus[userId] = {
            isOnline,
            updatedAt: new Date().toISOString(),
          };
          return;
        }
        return; // Skip this user
      }
      console.log(userId);
      // 3. You can still store separate online status if you want
      state.userOnlineStatus[userId] = {
        isOnline,
        updatedAt: new Date().toISOString(),
      };
    },

    // Snapshots replace presence: anyone absent from the list is offline.
    applyPresenceSnapshot: (state, action) => {
      const online = new Set(action.payload.onlineUserIds);
      const now = new Date().toISOString();

      state.users.forEach((user) => {
        const id = user?.other_participant_id ?? user?.id;
        if (id !== null && id !== undefined) user.is_online = online.has(id);
      });

      Object.keys(state.userOnlineStatus).forEach((id) => {
        if (!online.has(Number(id))) {
          state.userOnlineStatus[id] = { isOnline: false, updatedAt: now };
        }
      });

      online.forEach((id) => {
        state.userOnlineStatus[id] = { isOnline: true, updatedAt: now };
      });
    },

    // NEW: Bulk update online statuses (useful when initially loading users)
    updateBulkUserOnlineStatus: (state, action) => {
      const { statuses } = action.payload;
      console.log("Bulk updating user online statuses:", statuses);

      statuses.forEach(({ userId, isOnline }) => {
        state.userOnlineStatus[userId] = {
          isOnline,
          updatedAt: new Date().toISOString(),
        };
      });
    },

    // Unread counts
    incrementUnreadCount: (state, action) => {
      const { threadId, content } = action.payload;
      console.log(action.payload);

      // Increment unread count
      state.unreadCounts[threadId] = (state.unreadCounts[threadId] || 0) + 1;

      // Save latest message content
      state.latestMessages = state.latestMessages || {};
      state.latestMessages[threadId] = content;

      console.log(
        "Incremented unread count for thread:",
        threadId,
        "New count:",
        state.unreadCounts[threadId],
        "Latest content:",
        content
      );
    },

    clearUnreadCount: (state, action) => {
      const { threadId } = action.payload;
      state.unreadCounts[threadId] = 0;
      console.log("Cleared unread count for thread:", threadId);
    },

    //Update notification counts from backend signal
    updateNotificationCounts: (state, action) => {
      const { counts, timestamp } = action.payload;
      console.log("Updating notification counts from backend:", counts);

      // Update global count
      if (counts.global) {
        state.notificationCounts.global = {
          ...state.notificationCounts.global,
          ...counts.global,
        };
      }

      // Update firm counts
      if (counts.firm) {
        Object.keys(counts.firm).forEach((firmId) => {
          state.notificationCounts.firm[firmId] = counts.firm[firmId];
        });
      }

      // Update thread counts
      if (counts.thread) {
        Object.keys(counts.thread).forEach((threadId) => {
          state.notificationCounts.thread[threadId] = counts.thread[threadId];
        });
      }

      state.notificationCounts.lastUpdated = timestamp || Date.now();

      console.log("Updated notification counts:", {
        global: state.notificationCounts.global,
        firmCount: Object.keys(state.notificationCounts.firm).length,
        threadCount: Object.keys(state.notificationCounts.thread).length,
      });
    },

    // Clear notification counts for a specific context
    clearNotificationCounts: (state, action) => {
      const { type, id } = action.payload; // type: 'global' | 'firm' | 'thread', id for firm/thread

      if (type === "global") {
        state.notificationCounts.global.unread = 0;
      } else if (type === "firm" && id) {
        if (state.notificationCounts.firm[id]) {
          state.notificationCounts.firm[id] = 0;
        }
      } else if (type === "thread" && id) {
        if (state.notificationCounts.thread[id]) {
          state.notificationCounts.thread[id] = 0;
        }
      }

      console.log(`Cleared ${type} notification counts`, id ? `for ${id}` : "");
    },

    // Error handling
    clearError: (state, action) => {
      const { errorType } = action.payload;
      if (state.error[errorType]) {
        state.error[errorType] = null;
      }
    },

    // Set current user
    setCurrentUser: (state, action) => {
      state.currentUser = action.payload;
      console.log("Set current user:", action.payload);
    },

    // Thread management
    setThread: (state, action) => {
      const { threadId, thread } = action.payload;
      state.threads[threadId] = thread;
    },

    // Reset chat state (useful for logout)
    resetChatState: (state) => {
      Object.assign(state, initialState);
    },
  },

  extraReducers: (builder) => {
    // Fetch Firms
    builder
      .addCase(fetchFirms.pending, (state) => {
        state.loading.firms = true;
        state.error.firms = null;
      })
      .addCase(fetchFirms.fulfilled, (state, action) => {
        state.loading.firms = false;
        state.firms = action.payload;
      })
      .addCase(fetchFirms.rejected, (state, action) => {
        state.loading.firms = false;
        state.error.firms = action.payload;
      });

    // Only the latest organization's inbox request may update the visible list.
    [fetchUsers, fetchCoPilotUsers, fetchProviderClient].forEach((fetchInbox) => {
      builder
        .addCase(fetchInbox.pending, (state, action) => {
          state.activeUsersRequestId = action.meta.requestId;
          state.loading.users = true;
          state.error.users = null;
        })
        .addCase(fetchInbox.fulfilled, (state, action) => {
          if (state.activeUsersRequestId !== action.meta.requestId) return;
          state.activeUsersRequestId = null;
          state.loading.users = false;
          state.users = action.payload;
        })
        .addCase(fetchInbox.rejected, (state, action) => {
          if (state.activeUsersRequestId !== action.meta.requestId) return;
          state.activeUsersRequestId = null;
          state.loading.users = false;
          state.error.users = action.payload;
        });
    });

    // Create Thread
    builder
      .addCase(createThread.pending, (state) => {
        state.loading.creating = true;
      })
      .addCase(createThread.fulfilled, (state, action) => {
        state.loading.creating = false;
        const thread = action.payload;
        state.threads[thread.id] = thread;

        // Initialize messages for this thread
        state.messages[thread.id] = {
          items: [],
          hasMore: false,
          loading: false,
          oldestMessageId: null,
        };
      })
      .addCase(createThread.rejected, (state, action) => {
        state.loading.creating = false;
        state.error.general = action.payload;
      });

    builder
      .addCase(createProviderThread.pending, (state) => {
        state.loading.creating = true;
      })
      .addCase(createProviderThread.fulfilled, (state, action) => {
        state.loading.creating = false;
        const thread = action.payload;
        const caseProviderId = thread.case_provider_id || action.meta.arg.caseProviderId;
        const existingUser = state.users.find(
          (user) => String(user.case_provider_id) === String(caseProviderId)
        );
        const updatedUser = {
          ...existingUser,
          ...thread,
          case_provider_id: caseProviderId,
          has_thread: true,
          thread_id: thread.thread_id || thread.id,
          threadId: thread.threadId || thread.thread_id || thread.id,
        };

        state.threads[thread.id] = thread;
        if (
          !action.meta.arg.organizationKey ||
          action.meta.arg.organizationKey === getOrganizationKey(state.selectedFirm)
        ) {
          state.users = [
            updatedUser,
            ...state.users.filter(
              (user) => String(user.case_provider_id) !== String(caseProviderId)
            ),
          ];
        }

        // Initialize messages for this thread
        state.messages[thread.id] = {
          items: [],
          hasMore: false,
          loading: false,
          oldestMessageId: null,
        };
      })
      .addCase(createProviderThread.rejected, (state, action) => {
        state.loading.creating = false;
        state.error.general = action.payload;
      });

    // Fetch Messages
    builder
      .addCase(fetchMessages.pending, (state, action) => {
        const threadId = action.meta.arg.threadId;
        if (state.messages[threadId]) {
          state.messages[threadId].loading = true;
        }
      })
      .addCase(fetchMessages.fulfilled, (state, action) => {
        const { threadId, messages, before, hasMore } = action.payload;

        if (!state.messages[threadId]) {
          state.messages[threadId] = {
            items: [],
            hasMore: true,
            loading: false,
            oldestMessageId: null,
          };
        }

        const threadMessages = state.messages[threadId];
        threadMessages.loading = false;
        threadMessages.hasMore = hasMore;

        if (before) {
          // Prepending older messages (infinite scroll up)
          const newMessages = messages.filter(
            (newMsg) => !threadMessages.items.some((existingMsg) => existingMsg.id === newMsg.id)
          );
          threadMessages.items = [...newMessages, ...threadMessages.items];
        } else {
          // Initial load or refresh
          threadMessages.items = messages.map((msg) => ({
            ...msg,
            status: msg.status || "delivered",
          }));
        }

        // Update oldest message ID for pagination
        if (threadMessages.items.length > 0) {
          threadMessages.oldestMessageId = threadMessages.items[0].id;
        }

        // Sort messages by timestamp
        threadMessages.items.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
      })
      .addCase(fetchMessages.rejected, (state, action) => {
        const threadId = action.meta.arg.threadId;
        if (state.messages[threadId]) {
          state.messages[threadId].loading = false;
        }
        state.error.messages = action.payload;
      });

    // Mark Messages as Read
    builder
      .addCase(markMessagesAsRead.fulfilled, (state, action) => {
        const { threadId, messageIds } = action.payload;
        console.log("Mark as read fulfilled:", { threadId, messageIds });

        // Clear unread count
        state.unreadCounts[threadId] = 0;

        // Update message read status
        if (state.messages[threadId]) {
          state.messages[threadId].items.forEach((message) => {
            if (messageIds.includes(message.id)) {
              message.read = true;
              message.status = "read";
            }
          });
        }
      })
      .addCase(markMessagesAsRead.rejected, (state, action) => {
        console.error("Mark as read failed:", action.payload);
      });
  },
});

export const {
  setConnectionStatus,
  toggleChat,
  openChat,
  closeChat,
  minimizeChat,
  maximizeChat,
  setSelectedFirm,
  setSelectedUser,
  setUserSearch,
  addMessage,
  updateMessage,
  updateMessageStatus,
  updateMessageFromServer,
  markMessageFailed,
  addOptimisticMessage,
  setTypingUser,
  clearOldTypingIndicators,
  updateUserOnlineStatus,
  applyPresenceSnapshot,
  updateBulkUserOnlineStatus,
  incrementUnreadCount,
  clearUnreadCount,
  updateNotificationCounts, // NEW
  clearNotificationCounts, // NEW
  clearError,
  setCurrentUser,
  setThread,
  resetChatState,
} = ChatReducer.actions;

export default ChatReducer.reducer;
